import React from "react";
import { Box, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import * as moment from "moment";
import { connect, useSelector } from "react-redux";
import produce from "immer";
import VitalRow, { vitals as vitalsModel } from "./VitalRow";
import { tl } from "../../../components/translate";
import { RootState } from "../../../store";
import { IVitals } from "../../../interfaces/AssessmentInterfaces";

interface VitalsPlainInteraface {
  aVitals?: IVitals[];
  onSave: (data) => Promise<void>;
  vitalSettings?: any;
}

const VitalsPlain = ({ aVitals, onSave, vitalSettings }: VitalsPlainInteraface) => {
  const [saving, setSaving] = React.useState(false);
  const vitalsOrder =
    useSelector(
      (state: RootState) =>
        state.userContext?.resourceCentre?.settings?.assessmentSettings?.vitalsOrder
    ) || [];
  const defaultVitals: IVitals[] = [];

  if (vitalSettings) {
    vitalsOrder.forEach((item) => {
      if (vitalSettings[item]) {
        const vitalInfo = vitalsModel.find((vt) => vt.dbKey === item);
        if (vitalInfo)
          defaultVitals.push({
            name: vitalInfo.name,
            reading: vitalInfo.defaultValue,
            type: vitalInfo.type,
            unit: vitalInfo.unit,
            extraInfo: undefined,
            date: moment()
          });
      }
    });
  }

  const [vitals, setVitals] = React.useState<IVitals[] | null>(aVitals || null);

  React.useEffect(() => {
    if (aVitals?.length) {
      setVitals(aVitals);
    } else {
      setVitals(defaultVitals);
    }
  }, [aVitals]);

  return (
    <>
      <Box style={{ width: "100%", display: "flex" }}>
        <Box width="30%">
          <Typography style={{ fontWeight: 600 }}>{tl("assessment.vital")}</Typography>
        </Box>
        <Box width="30%" pl="20px">
          <Typography style={{ fontWeight: 600 }}>{tl("assessment.reading")}</Typography>
        </Box>
        <Box width="40%" pl="42px">
          <Typography style={{ fontWeight: 600 }}>{tl("assessment.extraInfo")}</Typography>
        </Box>
      </Box>
      <Box style={{ width: "100%" }}>
        {vitals?.map((vi, index) => (
          <VitalRow
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            vital={vi}
            onChange={(newObj) => {
              const updatedVItal = produce(vitals, (draft) => {
                draft[index] = newObj;
              });
              setVitals(updatedVItal);
            }}
            onRemove={() => {
              const newArr = produce(vitals, (draft) => {
                draft.splice(index, 1);
              });
              setVitals(newArr);
            }}
          />
        ))}
      </Box>
      <Box
        data-testmation="vitals.AddNewButton"
        onClick={() => {
          setVitals([
            ...vitals,
            {
              name: null,
              reading: null,
              unit: null,
              extraInfo: null,
              date: moment()
            }
          ]);
        }}
        style={{ cursor: "pointer", width: "80px" }}
      >
        <Typography>
          <u>{tl("assessment.addNew")}</u>
        </Typography>
      </Box>
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        padding="0px 24px"
        alignContent="center"
      >
        <Box />
        <Box>
          <LoadingButton
            data-testmation="vitalsSaveButton"
            variant="contained"
            color="primary"
            disabled={saving}
            loading={saving}
            onClick={async () => {
              setSaving(true);
              const filteredVitals = vitals?.filter((vi) => vi.name && vi.reading);
              await onSave(filteredVitals);
              setSaving(false);
            }}
          >
            {tl("assessment.save")}
          </LoadingButton>
        </Box>
      </Box>
    </>
  );
};

const mapStateToProps = (state) => {
  const rc =
    state.resources.resourceCentres.find((rC) => rC.id === state.userContext.resourceCentreId) ||
    state.userContext.resourceCentre;
  return {
    vitalSettings: rc?.settings.vitalSettings
  };
};

export default connect(mapStateToProps, null)(VitalsPlain);
